import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import { RelacionBadge, ESTADOS_INVERSOR, estadoRelacionLabel, normalizeEstadoRelacion } from './estadoRelacion';
import './InvestorPatientDetail.css';

interface InvestorPatient {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string | null;
  rut: string | null;
  prevision: string | null;
  activo: boolean;
  estado_relacion: string;
  total_consultas: number;
  ultima_consulta: string | null;
  nombre_terapeuta: string | null;
  porcentaje_inversionista: number;
  consulta_inicio_split: number;
}

interface InvestorPatientSession {
  id: string;
  fecha_hora: string;
  duracion: number;
  tipo_consulta: string;
  modalidad: string;
  estado: string;
  estado_pago: string;
  costo: number;
  nombre_terapeuta: string | null;
}

interface InvestorPatientDetailProps {
  investor: { id: string };
  patient: InvestorPatient;
  // Sólo el inversor fundador ve el control de edición del estado de relación.
  isFounder?: boolean;
  onBack: () => void;
  // Notifica al padre (InvestorPatients) para que la lista no quede stale.
  onEstadoChange?: (patientId: string, nuevoEstado: string) => void;
}

// Mismos labels que InvestorConsultas.tsx — no inventar variantes nuevas.
const TIPO_LABEL: Record<string, string> = {
  individual: 'Individual',
  pareja: 'Pareja',
  familiar: 'Familiar',
  sexologia: 'Sexología',
};

const MODALIDAD_LABEL: Record<string, string> = {
  presencial: 'Presencial',
  online: 'Online',
  domicilio: 'Domicilio',
};

// Valores reales de consultas.estado (CHECK constraint en prod) — 6, no 5:
// programada | confirmada | en_progreso | completada | cancelada | no_asistio.
const ESTADO_LABEL: Record<string, string> = {
  programada: 'Programada',
  confirmada: 'Confirmada',
  en_progreso: 'En progreso',
  completada: 'Completada',
  cancelada: 'Cancelada',
  no_asistio: 'No asistió',
};

// Punto de tipo de terapia — paleta WCAG AA fija del proyecto (CLAUDE.md),
// no son tokens Apple HIG, se usan literales a propósito.
const TIPO_DOT: Record<string, string> = {
  individual: '#1A6FBF',
  pareja: '#C05E00',
  familiar: '#057A55',
  sexologia: '#6B21A8',
};

// Valores reales de consultas.estado_pago_consulta (CHECK constraint en
// prod) — NO son los mismos 4 valores que pagos.estado_pago en
// InvestorRevenue.tsx (pagado/pendiente/parcial/reembolsado), son de otra
// columna/tabla. Confirmado contra prod: sin_pago | anticipo_pendiente |
// anticipo_pagado | pago_completo. Colores literales (no var(--token))
// porque se concatenan con alpha (`${color}18`) para el fondo del badge —
// un var() no admite esa concatenación.
const ESTADO_PAGO_LABEL: Record<string, string> = {
  sin_pago: 'Sin pago',
  anticipo_pendiente: 'Anticipo pendiente',
  anticipo_pagado: 'Anticipo pagado',
  pago_completo: 'Pago completo',
};

const ESTADO_PAGO_COLOR: Record<string, string> = {
  sin_pago: '#6e6e73',
  anticipo_pendiente: '#C05E00',
  anticipo_pagado: '#1A6FBF',
  pago_completo: '#057A55',
};

const formatMoney = (n: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);

const InvestorPatientDetail: React.FC<InvestorPatientDetailProps> = ({
  investor, patient, isFounder = false, onBack, onEstadoChange,
}) => {
  const [sessions, setSessions] = useState<InvestorPatientSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estado de relación — editable sólo por el inversor fundador.
  const estadoActual = normalizeEstadoRelacion(patient.estado_relacion);
  const [estadoDraft, setEstadoDraft] = useState<string>(estadoActual);
  const [estadoSaving, setEstadoSaving] = useState(false);
  const [estadoError, setEstadoError] = useState<string | null>(null);
  const [estadoSaved, setEstadoSaved] = useState(false);

  useEffect(() => {
    setEstadoDraft(normalizeEstadoRelacion(patient.estado_relacion));
    setEstadoError(null);
    setEstadoSaved(false);
  }, [patient.id, patient.estado_relacion]);

  const handleEstadoSave = async () => {
    if (estadoDraft === estadoActual) return;
    setEstadoSaving(true);
    setEstadoError(null);
    setEstadoSaved(false);
    const { error: rpcError } = await supabase.rpc('set_patient_relationship_status', {
      p_patient_id: patient.id,
      p_therapist_id: null,
      p_estado: estadoDraft,
    });
    setEstadoSaving(false);
    if (rpcError) {
      console.error('Error actualizando estado de relación:', rpcError.code);
      setEstadoError('No se pudo actualizar el estado. Reintentá.');
      return;
    }
    setEstadoSaved(true);
    onEstadoChange?.(patient.id, estadoDraft);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase.rpc('get_investor_patient_sessions', {
        p_inversionista_id: investor.id,
        p_paciente_id: patient.id,
      });
      if (rpcError) {
        console.error('Error cargando historial de sesiones:', rpcError.code);
        setError('No se pudo cargar el historial de sesiones.');
      } else {
        setSessions((data as InvestorPatientSession[]) ?? []);
      }
      setLoading(false);
    };
    load();
  }, [investor.id, patient.id]);

  const costoAcumulado = sessions
    .filter(s => s.estado !== 'cancelada')
    .reduce((sum, s) => sum + (s.costo ?? 0), 0);

  const getInitials = (nombre: string, apellido: string) =>
    `${nombre[0] ?? ''}${apellido[0] ?? ''}`.toUpperCase();

  return (
    <div className="investor-patient-detail">
      <button type="button" className="ipd-back-btn" onClick={onBack}>
        <i className="fas fa-arrow-left" /> Volver
      </button>

      <div className="ipd-header">
        <span className="patient-avatar-large patient-avatar--initials">
          {getInitials(patient.nombre, patient.apellido)}
        </span>
        <div className="ipd-header__info">
          <h1 className="ipd-header__name">{patient.nombre} {patient.apellido}</h1>
          {patient.rut && <p className="ipd-header__rut">{patient.rut}</p>}
          <div className="ipd-header__badges">
            <RelacionBadge estado={patient.estado_relacion} />
            <span className="ipd-badge ipd-badge--participacion">
              {patient.porcentaje_inversionista}% participación
            </span>
          </div>

          {isFounder && (
            <div className="ipd-estado-control">
              <label className="ipd-estado-control__label" htmlFor="ipd-estado-rel">
                Cambiar estado de la relación
              </label>
              <div className="ipd-estado-control__row">
                <select
                  id="ipd-estado-rel"
                  className="ipd-estado-control__select"
                  value={estadoDraft}
                  disabled={estadoSaving}
                  onChange={(e) => {
                    setEstadoDraft(e.target.value);
                    setEstadoError(null);
                    setEstadoSaved(false);
                  }}
                >
                  {/* Si el terapeuta lo dejó en un estado clínico (alta/derivado),
                      se muestra deshabilitado para no perder contexto — el inversor
                      sólo puede moverlo a estados operativos. */}
                  {!ESTADOS_INVERSOR.includes(estadoActual) && (
                    <option value={estadoActual} disabled>
                      {estadoRelacionLabel(estadoActual)} · fijado por el terapeuta
                    </option>
                  )}
                  {ESTADOS_INVERSOR.map((e) => (
                    <option key={e} value={e}>{estadoRelacionLabel(e)}</option>
                  ))}
                </select>
                {estadoDraft !== estadoActual && (
                  <button
                    type="button"
                    className="ipd-estado-control__save"
                    onClick={handleEstadoSave}
                    disabled={estadoSaving}
                  >
                    {estadoSaving ? 'Guardando…' : 'Guardar'}
                  </button>
                )}
                {estadoSaved && (
                  <span className="ipd-estado-control__ok">
                    <i className="fas fa-check-circle" aria-hidden="true" /> Guardado
                  </span>
                )}
              </div>
              {estadoError && (
                <span className="ipd-estado-control__error" role="alert">{estadoError}</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="ipd-quick-stats">
        <div className="ipd-quick-stat">
          <span className="ipd-quick-stat__value">{patient.total_consultas}</span>
          <span className="ipd-quick-stat__label">Sesiones totales</span>
        </div>
        <div className="ipd-quick-stat">
          <span className="ipd-quick-stat__value">
            {patient.ultima_consulta
              ? new Date(patient.ultima_consulta).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })
              : '—'}
          </span>
          <span className="ipd-quick-stat__label">Última sesión</span>
        </div>
        <div className="ipd-quick-stat">
          <span className="ipd-quick-stat__value">{patient.porcentaje_inversionista}%</span>
          <span className="ipd-quick-stat__label">Participación</span>
        </div>
        <div className="ipd-quick-stat">
          <span className="ipd-quick-stat__value">{formatMoney(costoAcumulado)}</span>
          <span className="ipd-quick-stat__label">Costo acumulado</span>
        </div>
      </div>

      <section className="ipd-sessions" aria-labelledby="ipd-sessions-heading">
        <div className="ipd-sessions__header">
          <h2 id="ipd-sessions-heading">Historial de Sesiones</h2>
          {!loading && !error && <span className="ipd-sessions__count">{sessions.length}</span>}
        </div>

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Cargando historial...</p>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="ipd-error">{error}</div>
        )}

        {!loading && !error && sessions.length === 0 && (
          <div className="ipd-empty">
            <i className="fas fa-calendar-times" />
            Este paciente aún no tiene sesiones registradas.
          </div>
        )}

        {!loading && !error && sessions.length > 0 && (
          <ul className="ipd-sessions__list">
            {sessions.map(s => {
              const fecha = new Date(s.fecha_hora);
              const pagoColor = ESTADO_PAGO_COLOR[s.estado_pago] ?? '#6e6e73';
              return (
                <li key={s.id} className={`ipd-session-row${s.estado === 'cancelada' ? ' ipd-session-row--cancelada' : ''}`}>
                  <div className="ipd-session-date">
                    <span className="ipd-session-date__day">{fecha.getDate()}</span>
                    <span className="ipd-session-date__month">
                      {fecha.toLocaleDateString('es-CL', { month: 'short', year: '2-digit' })}
                    </span>
                    <span className="ipd-session-date__time">
                      {fecha.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  <div className="ipd-session-meta">
                    <span>
                      <span
                        className="ipd-session-tipo-dot"
                        style={{ background: TIPO_DOT[s.tipo_consulta] ?? 'var(--text-muted)' }}
                      />
                      {TIPO_LABEL[s.tipo_consulta] ?? s.tipo_consulta}
                    </span>
                    <span>{s.duracion} min</span>
                    <span>{MODALIDAD_LABEL[s.modalidad] ?? s.modalidad}</span>
                    {s.nombre_terapeuta && <span>{s.nombre_terapeuta}</span>}
                    <span className={`ipd-badge ipd-badge--estado-${s.estado}`}>
                      {ESTADO_LABEL[s.estado] ?? s.estado}
                    </span>
                    <span
                      className="ipd-badge"
                      style={{ color: pagoColor, background: `${pagoColor}18` }}
                    >
                      {ESTADO_PAGO_LABEL[s.estado_pago] ?? s.estado_pago}
                    </span>
                  </div>

                  <div className="ipd-session-costo">{formatMoney(s.costo)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default InvestorPatientDetail;
